import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail as MailIcon, CheckCircle, Copy, ExternalLink, LogOut, DollarSign, MousePointerClick, TrendingUp, ShoppingBag } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";

function PortalShell({ children }) {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="container-custom py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="DCA" className="h-9 w-auto" />
            <span className="text-xs font-bold tracking-wider text-[#475569] hidden sm:block">AFFILIATE PORTAL</span>
          </a>
        </div>
      </header>
      <main className="container-custom py-8">{children}</main>
    </div>
  );
}

export function AffiliatePortalLogin() {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/affiliate-portal/login/request`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send sign-in link');
    } finally {
      setSending(false);
    }
  };

  return (
    <PortalShell>
      <div className="max-w-md mx-auto bg-white border border-[#E2E8F0] rounded-xl p-6 lg:p-8 shadow-sm">
        {sent ? (
          <div className="text-center" data-testid="affiliate-login-sent">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={28} className="text-emerald-600" />
            </div>
            <h1 className="heading-2 text-2xl text-[#0A0F1C] mb-2">Check your inbox</h1>
            <p className="text-sm text-[#475569]">If an active affiliate account matches that email, we just sent you a one-tap sign-in link. The link expires in 15 minutes.</p>
          </div>
        ) : (
          <>
            <h1 className="heading-2 text-2xl text-[#0A0F1C] mb-2" data-testid="affiliate-login-title">Affiliate Portal</h1>
            <p className="text-sm text-[#475569] mb-5">Enter the email associated with your affiliate account and we will send you a sign-in link.</p>
            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-1.5 inline-block">Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm text-[#0A0F1C] focus:border-[#1D4ED8] focus:ring-2 focus:ring-[#1D4ED8]/20 outline-none"
                  data-testid="affiliate-login-email"
                />
              </label>
              <button type="submit" disabled={sending} className="btn-primary-blue inline-flex items-center justify-center gap-2 w-full" data-testid="affiliate-login-submit">
                {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><MailIcon size={16} /> Send sign-in link</>}
              </button>
            </form>
          </>
        )}
      </div>
    </PortalShell>
  );
}

export function AffiliatePortalVerify() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = params.get('token');
    if (!token) { setStatus('error'); setError('Missing token'); return; }
    axios.get(`${API_URL}/api/affiliate-portal/login/verify?token=${encodeURIComponent(token)}`, { withCredentials: true })
      .then(() => { setStatus('ok'); setTimeout(() => navigate('/affiliate-portal'), 1200); })
      .catch((err) => { setStatus('error'); setError(err.response?.data?.detail || 'Sign-in failed'); });
  }, [params, navigate]);

  return (
    <PortalShell>
      <div className="max-w-md mx-auto text-center py-12">
        {status === 'verifying' && <><Loader2 size={32} className="animate-spin text-[#1D4ED8] mx-auto mb-4" /><p className="text-[#475569]">Signing you in…</p></>}
        {status === 'ok' && <><CheckCircle size={32} className="text-emerald-600 mx-auto mb-4" /><p className="text-[#0A0F1C] font-bold">Signed in. Redirecting…</p></>}
        {status === 'error' && <><div className="text-red-600 font-bold mb-2">Could not sign you in</div><p className="text-sm text-[#475569]">{error}</p><a href="/affiliate-portal/login" className="mt-4 inline-block btn-outline">Request a new link</a></>}
      </div>
    </PortalShell>
  );
}

export default function AffiliatePortalDashboard() {
  const [me, setMe] = useState(null);
  const [conversions, setConversions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState('paypal');
  const [payoutTo, setPayoutTo] = useState('');
  const [requesting, setRequesting] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [m, c, p] = await Promise.all([
        axios.get(`${API_URL}/api/affiliate-portal/me`, { withCredentials: true }),
        axios.get(`${API_URL}/api/affiliate-portal/conversions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/affiliate-portal/payouts`, { withCredentials: true }),
      ]);
      setMe(m.data); setConversions(c.data || []); setPayouts(p.data || []);
    } catch (err) {
      if (err.response?.status === 401) navigate('/affiliate-portal/login');
      else toast.error(err.response?.data?.detail || 'Failed to load portal');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const logout = async () => {
    await axios.post(`${API_URL}/api/affiliate-portal/logout`, {}, { withCredentials: true });
    navigate('/affiliate-portal/login');
  };

  const copyShare = async () => {
    if (!me?.share_url) return;
    try { await navigator.clipboard.writeText(me.share_url); toast.success('Share link copied'); } catch { toast.error('Copy failed'); }
  };

  const requestPayout = async (e) => {
    e.preventDefault();
    setRequesting(true);
    try {
      await axios.post(`${API_URL}/api/affiliate-portal/payouts/request`, { method, payout_to: payoutTo.trim() }, { withCredentials: true });
      toast.success('Payout requested');
      setPayoutTo('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payout request failed');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) return <PortalShell><div className="text-center py-16 text-[#94A3B8]"><Loader2 className="inline animate-spin mr-2" /> Loading portal…</div></PortalShell>;
  if (!me) return null;

  return (
    <PortalShell>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="heading-2 text-2xl lg:text-3xl text-[#0A0F1C]" data-testid="affiliate-portal-greeting">Welcome back, {me.name}.</h1>
            <p className="text-sm text-[#475569]">Code: <code className="bg-[#F1F5F9] px-1.5 py-0.5 rounded font-mono text-[#0A0F1C]">{me.code}</code> · {me.commission_pct}% commission</p>
          </div>
          <button onClick={logout} className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid="affiliate-logout"><LogOut size={14} /> Sign out</button>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={MousePointerClick} label="Clicks" value={me.clicks} accent="#1D4ED8" testid="stat-clicks" />
          <StatCard icon={ShoppingBag} label="Conversions" value={me.conversion_count} accent="#1D4ED8" testid="stat-conversions" />
          <StatCard icon={TrendingUp} label="Revenue driven" value={`$${(me.total_revenue || 0).toLocaleString()}`} accent="#F97316" testid="stat-revenue" />
          <StatCard icon={DollarSign} label="Outstanding" value={`$${(me.outstanding_balance || 0).toLocaleString()}`} accent="#10B981" testid="stat-outstanding" />
        </div>

        {/* Share link */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-[#475569] mb-2">Your share link</div>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="flex-1 min-w-0 truncate bg-[#F8FAFC] border border-[#E2E8F0] px-3 py-2 rounded text-xs font-mono text-[#0A0F1C]" data-testid="share-link">{me.share_url}</code>
            <button onClick={copyShare} className="btn-outline text-sm inline-flex items-center gap-1.5"><Copy size={14} /> Copy</button>
            <a href={me.share_url} target="_blank" rel="noopener noreferrer" className="btn-outline text-sm inline-flex items-center gap-1.5"><ExternalLink size={14} /> Open</a>
          </div>
          <p className="text-xs text-[#94A3B8] mt-2">Every visitor who clicks this link gets tracked for 90 days. You earn {me.commission_pct}% on every paid purchase.</p>
        </div>

        {/* Payout request */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h2 className="font-bold text-[#0A0F1C] mb-3">Request a payout</h2>
          <p className="text-sm text-[#475569] mb-4">Minimum payout is $25. Your current outstanding balance is <strong>${(me.outstanding_balance || 0).toLocaleString()}</strong>.</p>
          <form onSubmit={requestPayout} className="grid sm:grid-cols-2 gap-3">
            <select value={method} onChange={(e) => setMethod(e.target.value)} className="px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" data-testid="payout-method">
              <option value="paypal">PayPal</option>
              <option value="venmo">Venmo</option>
              <option value="zelle">Zelle</option>
              <option value="ach">ACH / Bank transfer</option>
            </select>
            <input
              type="text"
              required
              value={payoutTo}
              onChange={(e) => setPayoutTo(e.target.value)}
              placeholder="Email, handle, or last 4 of bank account"
              className="px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
              data-testid="payout-to"
            />
            <button type="submit" disabled={requesting || (me.outstanding_balance || 0) < 25} className="btn-primary-orange sm:col-span-2 inline-flex items-center justify-center gap-2" data-testid="payout-submit">
              {requesting ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : 'Request payout'}
            </button>
          </form>
        </div>

        {/* Conversions */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E2E8F0]"><h2 className="font-bold text-[#0A0F1C]">Conversions ({conversions.length})</h2></div>
          {conversions.length === 0 ? (
            <div className="text-center py-10 text-sm text-[#94A3B8]">No conversions yet. Share your link and start earning.</div>
          ) : (
            <div className="divide-y divide-[#F1F5F9]" data-testid="affiliate-conversions-list">
              {conversions.map((c) => (
                <div key={c.conversion_id} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0A0F1C] truncate">{c.item_name}</div>
                    <div className="text-[11px] text-[#94A3B8]">{(c.created_at || '').slice(0, 10)} · {c.item_type}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-emerald-700">+${(c.commission_amount || 0).toLocaleString()}</div>
                    <div className="text-[11px] text-[#94A3B8]">of ${(c.amount || 0).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payouts history */}
        {payouts.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E2E8F0]"><h2 className="font-bold text-[#0A0F1C]">Payout history</h2></div>
            <div className="divide-y divide-[#F1F5F9]">
              {payouts.map((p) => (
                <div key={p.payout_id} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-[#0A0F1C]">${(p.amount || 0).toLocaleString()} via {p.method.toUpperCase()}</div>
                    <div className="text-[11px] text-[#94A3B8]">Requested {(p.requested_at || '').slice(0, 10)} → {p.payout_to}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${p.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : p.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{p.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PortalShell>
  );
}

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} style={{ color: accent }} />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</span>
      </div>
      <div className="text-2xl font-extrabold text-[#0A0F1C]">{value}</div>
    </div>
  );
}
